import React, { useEffect, useState } from "react";
import { action, observable, toJS } from "mobx";
import { observer } from "mobx-react";
import { ReactComponent as LinkedInIcon } from "Pages/Dashboard/settings/ConnectedAccounts/LinkedInAccounts/svg/linkedIn.svg";
import Utils from "Utils/utils";
import LinkedInChat, { OwnerChip } from "Components/common/LinkedInChat";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";

class LinkedInReplyPopup {
  @observable showPopup = false;
  @observable liMessages = [];
  @observable editorLoading = true;
  @observable contactSequenceId = "";
  @observable contactId = "";
  @observable linkedinAccountId = "";
  @observable isOldLinkedInAccount = false;
  @observable isViewerOwner = false;

  @action
  setIsViewerOwner = (value = false) => {
    this.isViewerOwner = value;
  };

  @action
  setLinkedinAccountId = (value = "") => {
    this.linkedinAccountId = value;
  };

  @action
  setIsOldLinkedInAccount = (value = false) => {
    this.isOldLinkedInAccount = value;
  };

  @action
  setContactSequenceId = (value = "") => {
    this.contactSequenceId = value;
  };

  @action
  setContactId = (value = "") => {
    this.contactId = value;
  };

  @action
  setShowPopup = (value = "") => {
    this.showPopup = value;
  };

  @action
  setLiMessages = (value = []) => {
    this.liMessages = value;
  };

  @action
  setEditorLoading = (value = false) => {
    this.editorLoading = value;
  };

  @observable canViewLiReplies = false;
  @observable ownerName = "";

  @action
  setCanViewLiReplies = (value = false) => {
    this.canViewLiReplies = value;
  };

  @action
  setOwnerName = (value = "") => {
    this.ownerName = value;
  };

  @observable ownerProfileUrl = "";

  @action
  setOwnerProfileUrl = (value = "") => {
    this.ownerProfileUrl = value;
  };

  @observable liConnected = false;

  @action
  setLiConnected = (value = false) => {
    this.liConnected = value;
  };

  @observable profilePictureUrl = "";

  @action
  setProfilePictureUrl = (value = "") => {
    this.profilePictureUrl = value;
  };
}

const linkedInReplyPopupState = new LinkedInReplyPopup();

const LinkedInReplyContentPopup = observer(() => {
  const [liMessageNotes, setLiMessageNotes] = useState("");
  const [sequenceId, setSequenceId] = useState(
    linkedInReplyPopupState?.contactSequenceId
  );
  const [isSendDisabled, setIsSendDisabled] = useState(false);
  const [liMessageContactId, setLiMessageContactId] = useState(
    linkedInReplyPopupState?.contactId
  );

  const sendLinkedInMessage = async () => {
    if (isSendDisabled) return;
    setIsSendDisabled(true);

    const timeZone = Utils.getCurrentUserTimeZone();
    let dueDate = Utils.createScheduleDate(new Date());
    let splittedTimezone = timeZone?.replace("UTC", "") || "";
    dueDate = `${dueDate}${splittedTimezone}`;

    try {
      let requestBody;
      if (sequenceId) {
        requestBody = {
          ssId: sequenceId,
          text: liMessageNotes,
          dueDateString: dueDate
        };
      } else if (liMessageContactId) {
        requestBody = {
          cid: liMessageContactId,
          text: liMessageNotes,
          dueDateString: dueDate
        };
      }

      const config = {
        url: `${URLS?.sendLinkedInMessage}`,
        method: "POST",
        data: requestBody
      };

      const response = await makeApi(config);
      const { data = {} } = response || {};
      const responseCode = +response?.data?.responseCode;

      if (responseCode === 200) {
        setLiMessageNotes("");
        let newObj = {
          senderName: data.senderName,
          updateTs: data.dueDate,
          imageUrl: data.profileImage,
          message: data.text
        };

        linkedInReplyPopupState.setLiMessages([
          newObj,
          ...toJS(linkedInReplyPopupState?.liMessages)
        ]);
        toasterState.setToastMsg("Message sent", "success");
      } else if (responseCode === 100) {
        toasterState.setToastMsg(
          "No active LinkedIn connection found. Please connect and try again.",
          "failure"
        );
      } else if (responseCode === 101) {
        toasterState.setToastMsg(
          "You cannot send a message to this contact. Please contact your admin.",
          "failure"
        );
      } else if (responseCode === 102) {
        toasterState.setToastMsg(
          "This contact can no longer receive messages from you.",
          "failure"
        );
      } else if (responseCode === 103) {
        toasterState.setToastMsg(
          "You've reached the LinkedIn daily message limit. Please try sending later.",
          "failure"
        );
      } else {
        Utils.showApiFailureResponse(response?.data);
      }
    } catch (error) {
      console.error("Error in sendLinkedInMessage:", error);
    } finally {
      setIsSendDisabled(false);
    }
  };

  useEffect(() => {
    if (linkedInReplyPopupState.contactSequenceId !== sequenceId) {
      setSequenceId(linkedInReplyPopupState.contactSequenceId);
    }
  }, [linkedInReplyPopupState.contactSequenceId]);

  useEffect(() => {
    if (linkedInReplyPopupState.contactId !== liMessageContactId) {
      setLiMessageContactId(linkedInReplyPopupState.contactId);
    }
  }, [linkedInReplyPopupState.contactId]);

  const s = linkedInReplyPopupState;
  const isOwner = s.isViewerOwner;
  const canReply = !s.isOldLinkedInAccount && s.canViewLiReplies && s.liConnected;
  const sending = isSendDisabled;
  const blockReason = !s.canViewLiReplies
    ? "You don’t have permission to reply to this conversation. Only the owner of the connected LinkedIn account can reply."
    : s.isOldLinkedInAccount
      ? "Replies aren’t available for this LinkedIn account."
      : !s.liConnected
        ? isOwner
          ? "Your LinkedIn account is disconnected. Reconnect to continue messaging."
          : `${s.ownerName}’s LinkedIn account is disconnected. Ask ${s.ownerName} to reconnect.`
        : "";

  const closePopup = () => {
    s.setShowPopup(false);
    setLiMessageNotes("");
    s.setLiMessages([]);
    s.setContactId("");
  };

  const send = () => {
    if (!canReply || sending || !liMessageNotes.trim()) return;
    sendLinkedInMessage();
  };

  return (
    s?.showPopup && (
      <>
        <div className="linkedInReplyPopupWrapper">
          <div className="card inboxV2LiCard">
            <div className="inboxV2LiHead">
              <span className="inboxV2LiHeadIcon">
                <LinkedInIcon />
              </span>
              <div className="inboxV2LiHeadMeta">
                <div className="inboxV2LiHeadTitle">LinkedIn conversation</div>
              </div>
              <button
                type="button"
                className="inboxV2Close"
                onClick={closePopup}
              >
                <i className="material-icons">close</i>
              </button>
            </div>

            <LinkedInChat
              messages={toJS(s.liMessages) || []}
              loading={s.editorLoading}
              canViewLiReplies={s.canViewLiReplies}
              liConnected={s.liConnected}
              isOwner={isOwner}
              ownerName={s.ownerName}
              ownerProfileUrl={s.ownerProfileUrl}
              profilePictureUrl={s.profilePictureUrl}
            />

            <div className="inboxV2LiComposer">
              {canReply && !isOwner && s.ownerName && (
                <div className="inboxV2LiComposerNote">
                  You're replying as{" "}
                  <OwnerChip
                    ownerName={s.ownerName}
                    ownerProfileUrl={s.ownerProfileUrl}
                    profilePictureUrl={s.profilePictureUrl}
                  />
                </div>
              )}
              {!s.editorLoading && !canReply && blockReason && (
                <div className="inboxV2LiBlockReason">{blockReason}</div>
              )}
              <div className="inboxV2LiInputBox">
                <textarea
                  value={liMessageNotes}
                  rows={2}
                  placeholder={
                    canReply
                      ? "Write a message..."
                      : "You can’t reply to this conversation"
                  }
                  disabled={!canReply || sending}
                  onChange={event => setLiMessageNotes(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      send();
                    }
                  }}
                />
                <div className="inboxV2LiComposerFooter">
                  <span className="inboxV2LiComposerHint">
                    Press Enter to send · Shift + Enter for a new line
                  </span>
                  <button
                    type="button"
                    className="inboxV2LiSendBtn"
                    disabled={!canReply || sending || !liMessageNotes.trim()}
                    onClick={send}
                  >
                    {sending ? "Sending..." : "Send"}
                    <i className="material-icons">send</i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="linkedInReplyPopupOverlay" onClick={closePopup}></div>
        </div>
      </>
    )
  );
});

export { LinkedInReplyContentPopup, linkedInReplyPopupState };
export default LinkedInReplyContentPopup;
